import i18next from '@/i18n'

export const PROCESSING_METHOD_TYPES = {
    POS: 'pos',
    MOTO: 'moto',
    ECOM: 'ecom',
    PAY_BY_LINK: 'payByLink',
    PAY_BY_APP: 'payByApp',
    VIRTUAL_TERMINAL: 'virtualTerminal'
} as const

export type ProcessingMethodType =
    (typeof PROCESSING_METHOD_TYPES)[keyof typeof PROCESSING_METHOD_TYPES]

export interface ProcessingMethod {
    name: ProcessingMethodType
    description: string
    percent: number
}

import {BusinessStructure} from './models/onboarding'

export const COMPANY_DETAILS_MANDATORY_FIELDS = {
    [BusinessStructure.COMPANY]: [
        'entityType',
        'companyName',
        'companyTradeName',
        'companyNumber',
        'numberOfEmployees'
    ],
    [BusinessStructure.SOLE_TRADER]: ['entityType', 'companyName', 'companyTradeName'],
    [BusinessStructure.CHARITY]: [
        'entityType',
        'companyName',
        'companyTradeName',
        'companyNumber',
        'charityNumber',
        'charityCommission',
        'numberOfEmployees'
    ],
    [BusinessStructure.PARTNERSHIP]: [
        'entityType',
        'companyName',
        'companyTradeName',
        'numberOfEmployees'
    ],
    [BusinessStructure.LLP]: [
        'entityType',
        'companyName',
        'companyTradeName',
        'companyNumber',
        'numberOfEmployees'
    ],
    [BusinessStructure.OTHER]: [
        'entityType',
        'customEntityType',
        'companyName',
        'companyTradeName',
        'numberOfEmployees'
    ]
} as const

export const COMPANY_CONTACTS_MANDATORY_FIELDS = {
    [BusinessStructure.COMPANY]: [
        'emailAddress',
        'telephoneNumber',
        'postalCode',
        'addressLine1',
        'townOrCity'
    ],
    [BusinessStructure.SOLE_TRADER]: ['emailAddress', 'telephoneNumber'],
    [BusinessStructure.CHARITY]: [
        'emailAddress',
        'telephoneNumber',
        'postalCode',
        'addressLine1',
        'townOrCity'
    ],
    [BusinessStructure.PARTNERSHIP]: ['emailAddress', 'telephoneNumber'],
    [BusinessStructure.LLP]: [
        'emailAddress',
        'telephoneNumber',
        'postalCode',
        'addressLine1',
        'townOrCity'
    ],
    [BusinessStructure.OTHER]: ['emailAddress', 'telephoneNumber']
} as const

export const OWNERS_DIRECTORS_MANDATORY_FIELDS = {
    [BusinessStructure.COMPANY]: {
        owner: [
            'title',
            'firstName',
            'surname',
            'dateOfBirth',
            'nationality',
            'emailAddress',
            'telephoneNumber',
            'ownershipPercentage',
            'postalCode',
            'addressLine1',
            'townOrCity'
        ],
        director: [
            'title',
            'firstName',
            'surname',
            'dateOfBirth',
            'nationality',
            'emailAddress',
            'telephoneNumber',
            'position',
            'agreementSignatory',
            'postalCode',
            'addressLine1',
            'townOrCity'
        ]
    },
    [BusinessStructure.SOLE_TRADER]: {
        owner: [
            'title',
            'firstName',
            'surname',
            'dateOfBirth',
            'nationality',
            'emailAddress',
            'telephoneNumber',
            'postalCode',
            'addressLine1',
            'townOrCity'
        ],
        director: []
    },
    [BusinessStructure.CHARITY]: {
        owner: [
            'title',
            'firstName',
            'surname',
            'dateOfBirth',
            'nationality',
            'emailAddress',
            'telephoneNumber',
            'ownershipPercentage',
            'postalCode',
            'addressLine1',
            'townOrCity'
        ],
        director: [
            'title',
            'firstName',
            'surname',
            'dateOfBirth',
            'nationality',
            'emailAddress',
            'telephoneNumber',
            'position',
            'agreementSignatory',
            'postalCode',
            'addressLine1',
            'townOrCity'
        ]
    },
    [BusinessStructure.PARTNERSHIP]: {
        owner: [
            'title',
            'firstName',
            'surname',
            'dateOfBirth',
            'nationality',
            'emailAddress',
            'telephoneNumber',
            'ownershipPercentage',
            'postalCode',
            'addressLine1',
            'townOrCity'
        ],
        director: []
    },
    [BusinessStructure.LLP]: {
        owner: [
            'title',
            'firstName',
            'surname',
            'dateOfBirth',
            'nationality',
            'emailAddress',
            'telephoneNumber',
            'ownershipPercentage',
            'postalCode',
            'addressLine1',
            'townOrCity'
        ],
        director: [
            'title',
            'firstName',
            'surname',
            'dateOfBirth',
            'nationality',
            'emailAddress',
            'telephoneNumber',
            'position',
            'agreementSignatory',
            'postalCode',
            'addressLine1',
            'townOrCity'
        ]
    },
    [BusinessStructure.OTHER]: {
        owner: [
            'title',
            'firstName',
            'surname',
            'dateOfBirth',
            'nationality',
            'emailAddress',
            'telephoneNumber',
            'ownershipPercentage',
            'postalCode',
            'addressLine1',
            'townOrCity'
        ],
        director: [
            'title',
            'firstName',
            'surname',
            'dateOfBirth',
            'nationality',
            'emailAddress',
            'telephoneNumber',
            'position',
            'agreementSignatory',
            'postalCode',
            'addressLine1',
            'townOrCity'
        ]
    }
} as const

export const BANK_DETAILS_MANDATORY_FIELDS = [
    'bankName',
    'bankAccountNumber',
    'sortCode',
    'accountName'
] as const

export const OUTLETS_INFORMATION_MANDATORY_FIELDS = [
    'systemDescriptor',
    'settlementsAccount',
    'productsYouSell',
    'postcode',
    'addressLine1',
    'townOrCity'
] as const

export const ONBOARDING_MANDATORY_FIELDS = {
    companyDetails: COMPANY_DETAILS_MANDATORY_FIELDS,
    companyContacts: COMPANY_CONTACTS_MANDATORY_FIELDS,
    ownersDirectors: OWNERS_DIRECTORS_MANDATORY_FIELDS,
    bankDetails: BANK_DETAILS_MANDATORY_FIELDS,
    outletsInformation: OUTLETS_INFORMATION_MANDATORY_FIELDS
} as const

export const ONBOARDING_API_ERROR_CODES = {
    DUPLICATE_PROCESS: 100103
} as const

export const COUNTRIES = [
    'United Kingdom',
    'Afghanistan',
    'Albania',
    'Algeria',
    'Andorra',
    'Angola',
    'Antigua and Barbuda',
    'Argentina',
    'Armenia',
    'Australia',
    'Austria',
    'Azerbaijan',
    'Bahamas',
    'Bahrain',
    'Bangladesh',
    'Barbados',
    'Belarus',
    'Belgium',
    'Belize',
    'Benin',
    'Bhutan',
    'Bolivia',
    'Bosnia and Herzegovina',
    'Botswana',
    'Brazil',
    'Brunei',
    'Bulgaria',
    'Burkina Faso',
    'Burundi',
    'Cambodia',
    'Cameroon',
    'Canada',
    'Cape Verde',
    'Central African Republic',
    'Chad',
    'Chile',
    'China',
    'Colombia',
    'Comoros',
    'Congo',
    'Costa Rica',
    'Croatia',
    'Cuba',
    'Cyprus',
    'Czech Republic',
    'Denmark',
    'Djibouti',
    'Dominica',
    'Dominican Republic',
    'East Timor',
    'Ecuador',
    'Egypt',
    'El Salvador',
    'Equatorial Guinea',
    'Eritrea',
    'Estonia',
    'Ethiopia',
    'Fiji',
    'Finland',
    'France',
    'Gabon',
    'Gambia',
    'Georgia',
    'Germany',
    'Ghana',
    'Greece',
    'Grenada',
    'Guatemala',
    'Guinea',
    'Guinea-Bissau',
    'Guyana',
    'Haiti',
    'Honduras',
    'Hungary',
    'Iceland',
    'India',
    'Indonesia',
    'Iran',
    'Iraq',
    'Ireland',
    'Israel',
    'Italy',
    'Ivory Coast',
    'Jamaica',
    'Japan',
    'Jordan',
    'Kazakhstan',
    'Kenya',
    'Kiribati',
    'Kosovo',
    'Kuwait',
    'Kyrgyzstan',
    'Laos',
    'Latvia',
    'Lebanon',
    'Lesotho',
    'Liberia',
    'Libya',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Macedonia',
    'Madagascar',
    'Malawi',
    'Malaysia',
    'Maldives',
    'Mali',
    'Malta',
    'Marshall Islands',
    'Mauritania',
    'Mauritius',
    'Mexico',
    'Micronesia',
    'Moldova',
    'Monaco',
    'Mongolia',
    'Montenegro',
    'Morocco',
    'Mozambique',
    'Myanmar',
    'Namibia',
    'Nauru',
    'Nepal',
    'Netherlands',
    'New Zealand',
    'Nicaragua',
    'Niger',
    'Nigeria',
    'North Korea',
    'Norway',
    'Oman',
    'Pakistan',
    'Palau',
    'Palestine',
    'Panama',
    'Papua New Guinea',
    'Paraguay',
    'Peru',
    'Philippines',
    'Poland',
    'Portugal',
    'Qatar',
    'Romania',
    'Russia',
    'Rwanda',
    'Saint Kitts and Nevis',
    'Saint Lucia',
    'Saint Vincent and the Grenadines',
    'Samoa',
    'San Marino',
    'Sao Tome and Principe',
    'Saudi Arabia',
    'Senegal',
    'Serbia',
    'Seychelles',
    'Sierra Leone',
    'Singapore',
    'Slovakia',
    'Slovenia',
    'Solomon Islands',
    'Somalia',
    'South Africa',
    'South Korea',
    'South Sudan',
    'Spain',
    'Sri Lanka',
    'Sudan',
    'Suriname',
    'Swaziland',
    'Sweden',
    'Switzerland',
    'Syria',
    'Taiwan',
    'Tajikistan',
    'Tanzania',
    'Thailand',
    'Togo',
    'Tonga',
    'Trinidad and Tobago',
    'Tunisia',
    'Turkey',
    'Turkmenistan',
    'Tuvalu',
    'Uganda',
    'Ukraine',
    'United Arab Emirates',
    'United States',
    'Uruguay',
    'Uzbekistan',
    'Vanuatu',
    'Vatican City',
    'Venezuela',
    'Vietnam',
    'Yemen',
    'Zambia',
    'Zimbabwe'
] as const

export const NATIONALITIES = [
    'British',
    'Afghan',
    'Albanian',
    'Algerian',
    'American',
    'Andorran',
    'Angolan',
    'Antiguans',
    'Argentinean',
    'Armenian',
    'Australian',
    'Austrian',
    'Azerbaijani',
    'Bahamian',
    'Bahraini',
    'Bangladeshi',
    'Barbadian',
    'Barbudans',
    'Belarusian',
    'Belgian',
    'Belizean',
    'Beninese',
    'Bhutanese',
    'Bolivian',
    'Bosnian',
    'Brazilian',
    'Bruneian',
    'Bulgarian',
    'Burkinabe',
    'Burmese',
    'Burundian',
    'Cambodian',
    'Cameroonian',
    'Canadian',
    'Cape Verdean',
    'Central African',
    'Chadian',
    'Chilean',
    'Chinese',
    'Colombian',
    'Comoran',
    'Congolese',
    'Costa Rican',
    'Croatian',
    'Cuban',
    'Cypriot',
    'Czech',
    'Danish',
    'Djibouti',
    'Dominican',
    'Dutch',
    'East Timorese',
    'Ecuadorean',
    'Egyptian',
    'Emirian',
    'Equatorial Guinean',
    'Eritrean',
    'Estonian',
    'Ethiopian',
    'Fijian',
    'Filipino',
    'Finnish',
    'French',
    'Gabonese',
    'Gambian',
    'Georgian',
    'German',
    'Ghanaian',
    'Greek',
    'Grenadian',
    'Guatemalan',
    'Guinea-Bissauan',
    'Guinean',
    'Guyanese',
    'Haitian',
    'Herzegovinian',
    'Honduran',
    'Hungarian',
    'Icelander',
    'Indian',
    'Indonesian',
    'Iranian',
    'Iraqi',
    'Irish',
    'Israeli',
    'Italian',
    'Ivorian',
    'Jamaican',
    'Japanese',
    'Jordanian',
    'Kazakhstani',
    'Kenyan',
    'Kittian and Nevisian',
    'Kuwaiti',
    'Kyrgyz',
    'Laotian',
    'Latvian',
    'Lebanese',
    'Liberian',
    'Libyan',
    'Liechtensteiner',
    'Lithuanian',
    'Luxembourger',
    'Macedonian',
    'Malagasy',
    'Malawian',
    'Malaysian',
    'Maldivan',
    'Malian',
    'Maltese',
    'Marshallese',
    'Mauritanian',
    'Mauritian',
    'Mexican',
    'Micronesian',
    'Moldovan',
    'Monacan',
    'Mongolian',
    'Moroccan',
    'Mosotho',
    'Motswana',
    'Mozambican',
    'Namibian',
    'Nauruan',
    'Nepalese',
    'New Zealander',
    'Ni-Vanuatu',
    'Nicaraguan',
    'Nigerian',
    'Nigerien',
    'North Korean',
    'Northern Irish',
    'Norwegian',
    'Omani',
    'Pakistani',
    'Palauan',
    'Panamanian',
    'Papua New Guinean',
    'Paraguayan',
    'Peruvian',
    'Polish',
    'Portuguese',
    'Qatari',
    'Romanian',
    'Russian',
    'Rwandan',
    'Saint Lucian',
    'Salvadoran',
    'Samoan',
    'San Marinese',
    'Sao Tomean',
    'Saudi',
    'Scottish',
    'Senegalese',
    'Serbian',
    'Seychellois',
    'Sierra Leonean',
    'Singaporean',
    'Slovakian',
    'Slovenian',
    'Solomon Islander',
    'Somali',
    'South African',
    'South Korean',
    'Spanish',
    'Sri Lankan',
    'Sudanese',
    'Surinamer',
    'Swazi',
    'Swedish',
    'Swiss',
    'Syrian',
    'Taiwanese',
    'Tajik',
    'Tanzanian',
    'Thai',
    'Togolese',
    'Tongan',
    'Trinidadian or Tobagonian',
    'Tunisian',
    'Turkish',
    'Tuvaluan',
    'Ugandan',
    'Ukrainian',
    'Uruguayan',
    'Uzbekistani',
    'Venezuelan',
    'Vietnamese',
    'Welsh',
    'Yemenite',
    'Zambian',
    'Zimbabwean'
] as const

export const getProcessingMethodsTemplate = (): Omit<ProcessingMethod, 'percent'>[] => [
    {
        name: PROCESSING_METHOD_TYPES.POS,
        description: i18next.t('OnboardingStore.processingMethods.pos')
    },
    {
        name: PROCESSING_METHOD_TYPES.MOTO,
        description: i18next.t('OnboardingStore.processingMethods.moto')
    },
    {
        name: PROCESSING_METHOD_TYPES.ECOM,
        description: i18next.t('OnboardingStore.processingMethods.ecom')
    },
    {
        name: PROCESSING_METHOD_TYPES.PAY_BY_LINK,
        description: i18next.t('OnboardingStore.processingMethods.payByLink')
    },
    {
        name: PROCESSING_METHOD_TYPES.PAY_BY_APP,
        description: i18next.t('OnboardingStore.processingMethods.payByApp')
    },
    {
        name: PROCESSING_METHOD_TYPES.VIRTUAL_TERMINAL,
        description: i18next.t('OnboardingStore.processingMethods.virtualTerminal')
    }
]
